import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/SubscriptionsService';
import { Subscription } from '../types';

export const useSubscription = (subscriptionId?: string) => {
    const queryClient = useQueryClient();

    const subscriptionQuery = useQuery({
        queryKey: ['subscription', subscriptionId],
        queryFn: () => fetch(subscriptionId!),
        enabled: !!subscriptionId
    });

    const createMutation = useMutation({
        mutationFn: (values: Subscription) => {
            const { created, updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: Subscription) => {
            if (!subscriptionId) throw new Error('subscriptionId is required parameter');
            const { created, updated, ...rest } = values;
            return update(subscriptionId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!subscriptionId) throw new Error('subscriptionId is required parameter');
            return deleteRecord(subscriptionId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
        }
    });

    return {
        record: subscriptionQuery.data,
        loading: subscriptionQuery.isLoading,
        fetchRecord: subscriptionQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};